import { useCallback, useMemo, useState, useContext } from 'react'
import { Button, Box, Typography } from '@mui/material'
import { SnackbarHelperContext } from '../../contexts/SnackbarHelperContext'
import setNestedValue from 'lodash.set'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import CheckIcon from '@mui/icons-material/Check'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { firebase_functions } from '../../common/firebaseConfig'
import MultiSlideDialog, { SlideStep } from '../common/MultiSlideDialog'
import MetaTraderAccountForm, { MetaTraderAccount } from '../forms/MetaTraderAccountForm'
import { captureEvent } from '../../posthog'

function MetaApiAccountsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [account, setAccount] = useState<MetaTraderAccount>({
    login: '',
    password: '',
    server: '',
    platform: 'mt4',
    nickname: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [createMetaApiAccountFunc, createMetaApiAccountLoading] = useHttpsCallable(
    firebase_functions,
    'createMetaApiAccount'
  )

  const handleClose = useCallback(() => {
    setAccount({
      login: '',
      password: '',
      server: '',
      platform: 'mt4',
      nickname: '',
    })
    setErrorMessage(null)
    setShowPassword(false)
    onClose()
  }, [onClose])

  const handleAccountKeyChange = useCallback((key: string, value: any) => {
    setAccount((prev) => setNestedValue({ ...prev }, key, value))
  }, [])

  const createMetaApiAccount = useCallback(async () => {
    try {
      setErrorMessage(null)
      const response: any = await createMetaApiAccountFunc(account)

      if (!response?.data?.status || response?.data?.status >= 400) {
        setErrorMessage(response?.data?.message || 'Failed to link MetaTrader account')
        setSnackbarSettings({
          severity: 'error',
          message: response?.data?.message || 'Failed to link MetaTrader account',
          autoHideDuration: 6000,
        })
        return false
      }

      setSnackbarSettings({
        severity: 'success',
        message: 'MetaTrader account connected successfully!',
        autoHideDuration: 6000,
      })
      captureEvent('trading_account_connected', {
        platform: account.platform,
      })

      return true
    } catch (error) {
      setErrorMessage('Failed to link MetaTrader account')
      setSnackbarSettings({
        severity: 'error',
        message: 'Failed to link MetaTrader account',
        autoHideDuration: 6000,
      })
      return false
    }
  }, [account, createMetaApiAccountFunc, setSnackbarSettings])

  const steps: SlideStep[] = useMemo(() => {
    return getSteps(account, createMetaApiAccount, createMetaApiAccountLoading, errorMessage)
  }, [account, createMetaApiAccount, createMetaApiAccountLoading, errorMessage])

  return (
    <MultiSlideDialog
      open={open}
      onClose={handleClose}
      title="Connect MetaTrader Account"
      steps={steps}
      slideProps={{
        account,
        onAccountKeyChange: handleAccountKeyChange,
        showPassword,
        setShowPassword,
      }}
      actionProps={{
        account,
        onCreateAccount: createMetaApiAccount,
        isCreating: createMetaApiAccountLoading,
        errorMessage,
      }}
    />
  )
}

// Slide Components
function SetupAccountSlide() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <AccountBalanceIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        Connect MetaTrader
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Connect your MetaTrader account to enable automated trading. Your account credentials are
        securely encrypted and used to connect to MetaTrader.
      </Typography>
    </Box>
  )
}

function AccountFormSlide({ account, onAccountKeyChange, showPassword, setShowPassword }: any) {
  return (
    <MetaTraderAccountForm
      account={account}
      onAccountKeyChange={onAccountKeyChange}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
    />
  )
}

function AccountCreatedConfirmationSlide() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      px={2}
      alignItems="center"
      textAlign="center"
    >
      <CheckIcon sx={{ fontSize: '64px' }} color="success" />
      <Typography variant="h4" fontWeight="bold">
        Account Connected!
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Your MetaTrader account has been successfully connected. You can now use it for automated
        trading with your alerts.
      </Typography>
    </Box>
  )
}

function getSteps(
  account: MetaTraderAccount,
  createMetaApiAccount: () => Promise<boolean>,
  createMetaApiAccountLoading: boolean,
  errorMessage: string | null
): SlideStep[] {
  const isFormValid = account.login && account.password && account.server
  const steps: SlideStep[] = []

  // Setup account intro slide
  steps.push({
    slideComponent: SetupAccountSlide,
    actionsComponent: ({ onNextStep }: any) => (
      <Box display="flex" justifyContent="flex-end" width="100%" height="100%" alignItems="center">
        <Button onClick={onNextStep} variant="contained" color="success">
          Begin Setup
        </Button>
      </Box>
    ),
  })

  // Account form slide
  steps.push({
    slideComponent: AccountFormSlide,
    slideName: 'Account Details',
    subtitle: 'Enter your MetaTrader account credentials to enable automated trading.',
    actionsComponent: ({ onNextStep }: any) => (
      <Box display="flex" gap={2} width="100%" height="100%" alignItems="center">
        {createMetaApiAccountLoading && (
          <Typography variant="body2" color="textSecondary" width="100%">
            This could take a few minutes to connect.
          </Typography>
        )}
        {errorMessage && (
          <Typography variant="body2" color="error" width="100%">
            {errorMessage}
          </Typography>
        )}
        {!errorMessage && !isFormValid && (
          <Typography variant="body2" color="textSecondary" width="100%">
            * Please fill in all required fields to continue.
          </Typography>
        )}
        <Box
          display="flex"
          width="100%"
          height="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            onClick={async () => {
              const success = await createMetaApiAccount()
              if (success) onNextStep()
            }}
            variant="contained"
            color="success"
            disabled={!isFormValid || createMetaApiAccountLoading}
          >
            {createMetaApiAccountLoading ? 'Connecting...' : 'Connect Account'}
          </Button>
        </Box>
      </Box>
    ),
  })

  // Confirmation slide
  steps.push({
    slideComponent: AccountCreatedConfirmationSlide,
    actionsComponent: ({ onClose }: any) => (
      <Box display="flex" justifyContent="center" width="100%" height="100%">
        <Button onClick={onClose} variant="contained" color="success">
          Done
        </Button>
      </Box>
    ),
  })

  return steps
}

export default MetaApiAccountsDialog
